import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, settingsAPI, emailAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, Pencil, Trash2, Mail, Calendar as CalendarIcon, 
  User, FileText, Clock, CheckCircle2, Circle, Flag, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  formatDate, getStatusClass, getFormStatusClass,
  DEFAULT_ASSESSMENT_STATUSES, FORM_STATUSES, getStatusIndex 
} from '../lib/utils';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientRes, settingsRes] = await Promise.all([
        clientsAPI.getOne(id),
        settingsAPI.get()
      ]);
      setClient(clientRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const assessmentStatuses = settings?.assessment_statuses || DEFAULT_ASSESSMENT_STATUSES;

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const updates = { status: newStatus };
      
      // Auto-set dates based on status
      const today = new Date().toISOString().split('T')[0];
      if (newStatus === 'Assessment Scheduled' && !client.scheduled_date) {
        updates.scheduled_date = today;
      }
      if (newStatus === 'Report Sent' && !client.report_sent_date) {
        updates.report_sent_date = today;
      }
      if (newStatus === 'File Closed' && !client.closed_date) {
        updates.closed_date = today;
      }
      
      const response = await clientsAPI.update(id, updates);
      setClient(response.data);
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = async (field, date) => {
    setSaving(true);
    try {
      const updates = { [field]: date ? format(date, 'yyyy-MM-dd') : null };
      const response = await clientsAPI.update(id, updates);
      setClient(response.data);
      toast.success('Date updated');
    } catch (error) {
      toast.error('Failed to update date');
    } finally {
      setSaving(false);
    }
  };

  const handleFormStatusChange = async (formType, newStatus) => {
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const updatedForms = client.online_forms.map(f => {
        if (f.form_type === formType) {
          const updates = { ...f, status: newStatus };
          if (newStatus === 'Sent' && !f.sent_date) updates.sent_date = today;
          if (newStatus === 'Received' && !f.received_date) updates.received_date = today;
          if (newStatus === 'Scored' && !f.scored_date) updates.scored_date = today;
          return updates;
        }
        return f;
      });
      
      const response = await clientsAPI.update(id, { online_forms: updatedForms });
      setClient(response.data);
      toast.success('Form status updated');
    } catch (error) {
      toast.error('Failed to update form status');
    } finally {
      setSaving(false);
    }
  };

  const handleNotesChange = async (notes) => {
    try {
      await clientsAPI.update(id, { notes });
      setClient(prev => ({ ...prev, notes }));
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handlePaymentStatusChange = async (status) => {
    setSaving(true);
    try {
      const response = await clientsAPI.update(id, { payment_status: status });
      setClient(response.data);
      toast.success('Payment status updated');
    } catch (error) {
      toast.error('Failed to update payment status');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSupervision = async () => {
    setSaving(true);
    try {
      const response = await clientsAPI.toggleSupervision(id);
      setClient(prev => ({ ...prev, supervision_needed: response.data.supervision_needed }));
      toast.success(response.data.supervision_needed ? 'Flagged for supervision' : 'Supervision flag removed');
    } catch (error) {
      toast.error('Failed to toggle supervision');
    } finally {
      setSaving(false);
    }
  };

  const handleAddForm = async (formType) => {
    if (client.online_forms.some(f => f.form_type === formType)) {
      toast.error('Form already added');
      return;
    }
    setSaving(true);
    try {
      const updatedForms = [...client.online_forms, { form_type: formType, status: 'Not Sent' }];
      const response = await clientsAPI.update(id, { online_forms: updatedForms });
      setClient(response.data);
      toast.success('Form added');
    } catch (error) {
      toast.error('Failed to add form');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForm = async (formType) => {
    setSaving(true);
    try {
      const updatedForms = client.online_forms.filter(f => f.form_type !== formType);
      const response = await clientsAPI.update(id, { online_forms: updatedForms });
      setClient(response.data);
      toast.success(`${formType} removed`);
    } catch (error) {
      toast.error('Failed to remove form');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${client.name}?`)) return;
    
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted');
      navigate('/clients');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleSendReminder = async (type) => {
    try {
      await emailAPI.sendReminder({ client_id: id, reminder_type: type });
      toast.success('Reminder email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (!client) return null;

  const currentStatusIndex = getStatusIndex(client.status, assessmentStatuses);
  const activeFormTypes = settings?.form_types?.filter(f => f.active).sort((a, b) => a.name.localeCompare(b.name)) || [];
  const availableFormsToAdd = activeFormTypes.filter(f => !client.online_forms.some(cf => cf.form_type === f.name));
  const sortedClientForms = [...client.online_forms].sort((a, b) => a.form_type.localeCompare(b.form_type));

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clients')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope']">{client.name}</h1>
                {client.supervision_needed && (
                  <Flag className="w-5 h-5 text-purple-600" />
                )}
              </div>
              <p className="text-slate-500">{client.client_id} • {client.assessment_type}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleSendReminder('upcoming')} data-testid="send-reminder-btn">
            <Mail className="w-4 h-4 mr-2" />
            Send Reminder
          </Button>
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/edit`)} data-testid="edit-client-btn">
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={handleDelete} data-testid="delete-client-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Client Info */}
        <div className="space-y-6">
          {/* Supervision Toggle - Prominent */}
          <Card className={`border ${client.supervision_needed ? 'border-purple-300 bg-purple-50' : 'border-slate-200'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Flag className={`w-5 h-5 ${client.supervision_needed ? 'text-purple-600' : 'text-slate-400'}`} />
                  <div>
                    <Label className="font-medium">Supervision Needed</Label>
                    <p className="text-sm text-slate-500">Flag for discussion</p>
                  </div>
                </div>
                <Switch
                  checked={client.supervision_needed}
                  onCheckedChange={handleToggleSupervision}
                  disabled={saving}
                  data-testid="supervision-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <User className="w-5 h-5" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium">{client.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Age</p>
                <p className="font-medium">{client.age_display || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">School</p>
                <p className="font-medium">{client.school || '-'}</p>
                {client.year_of_school && (
                  <p className="text-sm text-slate-500">Year {client.year_of_school}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500">Referrer</p>
                <p className="font-medium">{client.referrer || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Initial/Review</p>
                <p className="font-medium">{client.initial_or_review}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Supports</p>
                <p className="font-medium text-sm">{client.current_supports || '-'}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 mb-2">Payment Status</p>
                <Select value={client.payment_status} onValueChange={handlePaymentStatusChange}>
                  <SelectTrigger data-testid="payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Not Paid">Not Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Key Dates */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Key Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: 'Referral Date', field: 'referral_date', value: client.referral_date },
                { label: 'Assessment Session', field: 'scheduled_date', value: client.scheduled_date },
                { label: 'Parent Session', field: 'parent_session_date', value: client.parent_session_date },
                { label: 'Observation', field: 'observation_date', value: client.observation_date },
                { label: 'Assessment Completed', field: 'assessment_completion_date', value: client.assessment_completion_date },
                { label: 'Report Due', field: 'report_due_date', value: client.report_due_date, highlight: true },
                { label: 'Report Sent', field: 'report_sent_date', value: client.report_sent_date },
                { label: 'Feedback Session', field: 'feedback_date', value: client.feedback_date },
              ].map(({ label, field, value, highlight }) => (
                <div key={field} className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{label}</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className={highlight && value ? 'text-emerald-600' : ''}
                        data-testid={`date-picker-${field}`}
                      >
                        {value ? formatDate(value) : 'Set date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={value ? new Date(value) : undefined}
                        onSelect={(date) => handleDateChange(field, date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Progress & Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress Stepper */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Assessment Progress
                </CardTitle>
                <Select value={client.status} onValueChange={handleStatusChange} disabled={saving}>
                  <SelectTrigger className="w-64" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assessmentStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute top-4 left-4 right-4 h-0.5 bg-slate-200" />
                <div 
                  className="absolute top-4 left-4 h-0.5 bg-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, (currentStatusIndex / (assessmentStatuses.length - 1)) * 100)}%` }}
                />
                
                {/* Status Steps - Show key milestones */}
                <div className="relative flex justify-between">
                  {[0, 2, 4, 6, 10].map((stepIndex) => {
                    const status = assessmentStatuses[stepIndex] || '';
                    const isCompleted = currentStatusIndex > stepIndex;
                    const isCurrent = currentStatusIndex === stepIndex;
                    
                    return (
                      <div key={stepIndex} className="flex flex-col items-center">
                        <div className={`
                          w-8 h-8 rounded-full flex items-center justify-center z-10
                          ${isCompleted ? 'bg-emerald-500 text-white' : 
                            isCurrent ? 'bg-white border-2 border-emerald-500 text-emerald-500' : 
                            'bg-white border-2 border-slate-200 text-slate-400'}
                        `}>
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <Circle className="w-5 h-5" />
                          )}
                        </div>
                        <p className={`text-xs mt-2 text-center max-w-20 ${isCurrent ? 'font-medium text-slate-900' : 'text-slate-500'}`}>
                          {status.split(' ')[0]}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Current Status</p>
                <Badge className={`${getStatusClass(client.status)} mt-1`}>
                  {client.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Online Forms */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Online Forms
                </CardTitle>
                {availableFormsToAdd.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-form-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Form
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="end">
                      <div className="space-y-1">
                        {availableFormsToAdd.map((form) => (
                          <Button
                            key={form.name}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-sm"
                            onClick={() => handleAddForm(form.name)}
                          >
                            {form.name}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedClientForms.map((form) => (
                  <div 
                    key={form.form_type}
                    className="p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{form.form_type}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getFormStatusClass(form.status)} text-xs`}>
                          {form.status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteForm(form.form_type);
                          }}
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                          title="Remove form"
                          data-testid={`delete-form-${form.form_type}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Select 
                      value={form.status} 
                      onValueChange={(val) => handleFormStatusChange(form.form_type, val)}
                      disabled={saving}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`form-status-${form.form_type}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FORM_STATUSES.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              {sortedClientForms.length === 0 && (
                <p className="text-center text-slate-500 py-4">No forms assigned. Click "Add Form" to add.</p>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={client.notes || ''}
                onChange={(e) => setClient(prev => ({ ...prev, notes: e.target.value }))}
                onBlur={(e) => handleNotesChange(e.target.value)}
                placeholder="Add notes about this client..."
                className="min-h-32"
                data-testid="notes-textarea"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
